/**
 * The profile: everything that survives a run (doc 08), plus the rules for
 * changing it. Pearls and XP are banked the instant they are earned — a run
 * that dies six fights in keeps six paydays — so every mutation here is meant
 * to be followed immediately by a save.
 *
 * Migrations live here too, from day one (doc 12 §Saves): a profile read from
 * storage passes through `migrate` before anything else touches it.
 */

import audioJson from '../content/audio.json' with { type: 'json' };
import { clearAward, type Award, type ClearInput } from './pearls.ts';
import { activeTiers, pressureTier } from './pressure.ts';
import { costOf, FIRST_LOSS, NODES, XP_RATES } from './tree.ts';
import type { MotionChoice, Profile, RunLogEntry, Settings, Tutorial, UiScaleChoice } from './types.ts';
import { averageLevel, levelOf, waveXp } from './xp.ts';

/**
 * v2 (stage 5c) added `depthsCleared` and `propertiesSeen`. A v1 profile has
 * neither, and a v1 player with a win behind them has certainly cleared D1 —
 * so the migration hands it back rather than locking the trench (doc 12 §Saves).
 *
 * v3 (the tutorial) added `tutorial`. Every profile that existed before it has
 * already played, so onboarding arrives retired rather than re-teaching a
 * veteran their own colony (doc 19 §Lifecycle).
 */
export const SCHEMA_VERSION = 3;

const DEFAULT_SETTINGS: Settings = {
  assist: false,
  uiScale: 'auto',
  edgeScroll: true,
  minimap: true,
  volMaster: audioJson.defaults.master,
  volMusic: audioJson.defaults.music,
  volSfx: audioJson.defaults.sfx,
  volAmbience: audioJson.defaults.ambience,
  screenShake: true,
  flash: 'full',
  damageNumbers: true,
  reducedMotion: 'auto',
  autoLureSeconds: 0,
  confirmSell: false,
};

/** Doc 18 §7: twenty runs, oldest dropped. A ring buffer, not a history. */
export const RUN_LOG_SIZE = 20;

/** Doc 18 §2: the choices the auto-lure row offers, in seconds. 0 = never. */
export const AUTO_LURE_CHOICES = [0, 30, 15, 10, 5] as const;

export function newProfile(name: string, now: number): Profile {
  return {
    schemaVersion: SCHEMA_VERSION,
    name,
    createdAt: now,
    pearls: 0,
    pearlsEarned: 0,
    owned: [],
    towerXp: {},
    codex: [],
    firstClears: [],
    depthsCleared: [],
    propertiesSeen: [],
    pressure: {},
    runs: 0,
    wins: 0,
    losses: 0,
    assistLosses: 0,
    reveals: [],
    runLog: [],
    tutorial: { seen: [], released: [], done: false },
    settings: { ...DEFAULT_SETTINGS },
  };
}

// ---------------------------------------------------------------------------
// reading a profile back
// ---------------------------------------------------------------------------

function num(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function strArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

/** Doc 08 Pressure: depth → active tier ids. Unknown ids are dropped on load. */
function pressureRecord(v: unknown): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  if (v && typeof v === 'object') {
    for (const [depth, ids] of Object.entries(v as Record<string, unknown>)) {
      const kept = strArray(ids).filter((id) => pressureTier(id) !== undefined);
      if (kept.length > 0) out[depth] = kept;
    }
  }
  return out;
}

/**
 * Doc 19 §Lifecycle. A profile written before onboarding existed belongs to a
 * player who has already been taught by playing; arming the sequence for them
 * would be a lecture, so a pre-v3 profile with a run behind it arrives done.
 */
function tutorialState(v: unknown, version: number, runs: number): Tutorial {
  if (v && typeof v === 'object') {
    const t = v as Record<string, unknown>;
    return { seen: strArray(t.seen), released: strArray(t.released), done: t.done === true };
  }
  return { seen: [], released: [], done: version < 3 && runs > 0 };
}

/**
 * Settings grow every polish phase, so they are rebuilt field by field from the
 * defaults rather than trusted wholesale. A profile written by an older build
 * simply gets today's default for anything it never heard of — which is why
 * adding a row to doc 18 §2 has never needed a schema bump.
 */
function settingsOf(v: unknown): Settings {
  const out = { ...DEFAULT_SETTINGS };
  if (!v || typeof v !== 'object') return out;
  const r = v as Record<string, unknown>;
  out.assist = r.assist === true;
  out.edgeScroll = r.edgeScroll !== false;
  out.minimap = r.minimap !== false;
  out.confirmSell = r.confirmSell === true;
  out.screenShake = r.screenShake !== false;
  out.damageNumbers = r.damageNumbers !== false;
  if (r.flash === 'full' || r.flash === 'reduced' || r.flash === 'off') out.flash = r.flash;
  for (const key of ['volMaster', 'volMusic', 'volSfx', 'volAmbience'] as const) {
    const v = r[key];
    if (typeof v === 'number' && Number.isFinite(v)) out[key] = Math.max(0, Math.min(100, Math.round(v)));
  }
  if (r.uiScale === 'auto' || r.uiScale === 'small' || r.uiScale === 'normal' || r.uiScale === 'large') {
    out.uiScale = r.uiScale as UiScaleChoice;
  }
  if (r.reducedMotion === 'on' || r.reducedMotion === 'off' || r.reducedMotion === 'auto') {
    out.reducedMotion = r.reducedMotion as MotionChoice;
  }
  if (AUTO_LURE_CHOICES.includes(r.autoLureSeconds as (typeof AUTO_LURE_CHOICES)[number])) {
    out.autoLureSeconds = r.autoLureSeconds as number;
  }
  return out;
}

/** Doc 18 §7. A malformed entry is dropped, never guessed at. */
function runLog(v: unknown): RunLogEntry[] {
  if (!Array.isArray(v)) return [];
  const out: RunLogEntry[] = [];
  for (const raw of v.slice(-RUN_LOG_SIZE)) {
    if (!raw || typeof raw !== 'object') continue;
    const e = raw as Record<string, unknown>;
    const outcome = e.outcome;
    if (outcome !== 'won' && outcome !== 'lost' && outcome !== 'left') continue;
    out.push({
      depth: num(e.depth, 1),
      pulls: strArray(e.pulls),
      fightsCleared: num(e.fightsCleared, 0),
      towers: strArray(e.towers),
      outcome,
      corePct: num(e.corePct, 0),
      ticks: num(e.ticks, 0),
      pressure: strArray(e.pressure),
      assist: e.assist === true,
    });
  }
  return out;
}

function numRecord(v: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (v && typeof v === 'object') {
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
      if (typeof val === 'number' && Number.isFinite(val)) out[k] = val;
    }
  }
  return out;
}

/**
 * Rebuild a profile from whatever storage held. Unknown fields are dropped,
 * missing fields take defaults, and a version from the future is refused
 * (returning null) rather than being guessed at.
 */
export function migrate(raw: unknown, now: number): Profile | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const version = num(r.schemaVersion, 0);
  if (version > SCHEMA_VERSION) return null;

  const base = newProfile(typeof r.name === 'string' ? r.name : 'COLONY', num(r.createdAt, now));
  const p: Profile = {
    ...base,
    pearls: Math.max(0, Math.round(num(r.pearls, 0))),
    pearlsEarned: Math.max(0, Math.round(num(r.pearlsEarned, num(r.pearls, 0)))),
    owned: strArray(r.owned).filter((id) => NODES[id] !== undefined),
    towerXp: numRecord(r.towerXp),
    codex: strArray(r.codex),
    firstClears: strArray(r.firstClears),
    depthsCleared: Array.isArray(r.depthsCleared)
      ? r.depthsCleared.filter((d): d is number => typeof d === 'number')
      : version < 2 && num(r.wins, 0) > 0
        ? [1] // a v1 profile with a win has beaten the only depth that existed
        : [],
    propertiesSeen: strArray(r.propertiesSeen),
    pressure: pressureRecord(r.pressure),
    runs: Math.max(0, Math.round(num(r.runs, 0))),
    wins: Math.max(0, Math.round(num(r.wins, 0))),
    losses: Math.max(0, Math.round(num(r.losses, 0))),
    assistLosses: Math.max(0, Math.round(num(r.assistLosses, 0))),
    reveals: strArray(r.reveals),
    runLog: runLog(r.runLog),
    tutorial: tutorialState(r.tutorial, version, num(r.runs, 0)),
    settings: settingsOf(r.settings),
  };

  const run = r.run;
  if (run && typeof run === 'object') {
    const rr = run as Record<string, unknown>;
    if (typeof rr.snapshot === 'string' && rr.snapshot.length > 0) {
      p.run = {
        depth: num(rr.depth, 1),
        seed: num(rr.seed, 0),
        snapshot: rr.snapshot,
        savedAt: num(rr.savedAt, now),
        fightsCleared: num(rr.fightsCleared, 0),
      };
    }
  }
  p.schemaVersion = SCHEMA_VERSION;
  return p;
}

// ---------------------------------------------------------------------------
// spending
// ---------------------------------------------------------------------------

/** Buy a node. Returns null on success, or the reason it bounced. */
export function buyNode(p: Profile, id: string): string | null {
  const n = NODES[id];
  if (!n) return 'NO SUCH NODE';
  if (p.owned.includes(id)) return 'ALREADY OWNED';
  if (!n.available) return 'NOT IN THE WATER YET';
  const cost = costOf(id, p.reveals);
  if (p.pearls < cost) return `NEEDS ${cost} PEARLS`;
  p.pearls -= cost;
  p.owned.push(id);
  return null;
}

/** Doc 08's Hades rule: respec is free, instant, unlimited. Returns the refund. */
export function respec(p: Profile): number {
  let refund = 0;
  for (const id of p.owned) refund += costOf(id, p.reveals);
  p.owned = [];
  p.pearls += refund;
  return refund;
}

// ---------------------------------------------------------------------------
// earning
// ---------------------------------------------------------------------------

export interface ClearContext extends Omit<ClearInput, 'newSpecies' | 'firstClear'> {
  shoalId: string;
  /** Species killed during this run so far, by id — the Codex reads this. */
  killedSpecies: string[];
}

/** Bank one cleared shoal. Mutates the profile; the caller saves right after. */
export function awardClear(p: Profile, ctx: ClearContext): Award {
  const key = `${ctx.depth}:${ctx.shoalId}`;
  const firstClear = !p.firstClears.includes(key);
  const newSpecies = ctx.killedSpecies.filter((s) => !p.codex.includes(s));
  const tiers = activeTiers(p.pressure[String(ctx.depth)] ?? []).length;
  const award = clearAward({ ...ctx, newSpecies, firstClear, pressureTiers: tiers });
  p.pearls += award.pearls;
  p.pearlsEarned += award.pearls;
  if (firstClear) p.firstClears.push(key);
  for (const s of newSpecies) p.codex.push(s);
  return award;
}

/**
 * Bank a wave's tower XP. `damage` is that wave's damage per tower def and
 * `killers` the defs that landed at least one kill (doc 08).
 */
export function awardXp(
  p: Profile,
  damage: Record<string, number>,
  killers: string[],
): Record<string, number> {
  const avg = averageLevel(p.towerXp);
  const gained: Record<string, number> = {};
  const defs = new Set([...Object.keys(damage), ...killers]);
  for (const def of defs) {
    const have = p.towerXp[def] ?? 0;
    const xp = waveXp(damage[def] ?? 0, killers.includes(def), levelOf(have) < avg);
    if (xp <= 0) continue;
    p.towerXp[def] = have + xp;
    gained[def] = xp;
  }
  return gained;
}

/** Doc 08: 200 XP the first time a depth falls with that tower deployed. */
export function awardDepthClear(p: Profile, deployedDefs: string[]): Record<string, number> {
  const gained: Record<string, number> = {};
  for (const def of new Set(deployedDefs)) {
    p.towerXp[def] = (p.towerXp[def] ?? 0) + XP_RATES.firstDepthClear;
    gained[def] = XP_RATES.firstDepthClear;
  }
  return gained;
}

// ---------------------------------------------------------------------------
// run bookkeeping + the first-loss schedule
// ---------------------------------------------------------------------------

/** Doc 18 §7: append one run and drop the oldest past twenty. */
export function logRun(p: Profile, entry: RunLogEntry): void {
  p.runLog.push(entry);
  if (p.runLog.length > RUN_LOG_SIZE) p.runLog.splice(0, p.runLog.length - RUN_LOG_SIZE);
}

export interface Reveal {
  id: string;
  headline: string;
  text: string;
}

/**
 * Doc 08: the first three defeats each end with the player owning something new.
 * Applies the step's gift and returns it for the defeat screen to announce.
 */
export function registerRunEnd(p: Profile, won: boolean, depth = 1): Reveal | null {
  p.runs++;
  if (won) {
    p.wins++;
    // doc 09: a boss falling is what opens the next rung of the trench
    if (!p.depthsCleared.includes(depth)) p.depthsCleared.push(depth);
    return null;
  }
  p.losses++;
  if (p.settings.assist) p.assistLosses++;

  const step = FIRST_LOSS.find((s) => s.loss === p.losses && !p.reveals.includes(s.id));
  if (!step) return null;
  p.reveals.push(step.id);
  if (step.minPearls !== undefined && p.pearls < step.minPearls) {
    p.pearlsEarned += step.minPearls - p.pearls;
    p.pearls = step.minPearls;
  }
  if (step.grantsNode !== undefined && !p.owned.includes(step.grantsNode)) {
    p.owned.push(step.grantsNode);
  }
  return { id: step.id, headline: step.headline, text: step.text };
}
